import { Badge, Box, Menu, MenuItem } from '@mui/material';
import React from 'react';
import { useDrag } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { materialStateEnum } from '../../constants';
import { getMaterialStateColor } from '../../constants/colors';
import { useContextMenu } from '../../hooks/useContextMenu';
import { useTheme } from '../../hooks/useTheme';
import { capitalizeFirstLetter } from '../../utils/capitalize';

export interface MenuItemDraggable {
    label: string;
    shortcut: string;
    action: (() => void) | undefined;
}

interface DraggableCellProps {
    dragIds: { lineId: string; itemId: string };
    index: number;
    onDragStart?: () => void;
    itemType: string;
    children: React.ReactNode;
    onDelete?: () => void;
    disabled?: boolean;
    state?: number;
    onDuplicate?: () => void;
    onDoubleClick?: () => void;
    warning?: boolean;
    warningChar?: string;
    menuItems?: MenuItemDraggable[];
    disableMenu?: boolean;
}

const DraggableCell: React.FC<DraggableCellProps> = ({
    children,
    dragIds,
    itemType,
    onDragStart = () => {},
    onDelete = () => {},
    disabled = false,
    state = materialStateEnum.PRESENT,
    onDuplicate = () => {},
    onDoubleClick,
    warning = false,
    warningChar = '!',
    menuItems = [],
    disableMenu = false,
}) => {
    const { t } = useTranslation();
    const { mode } = useTheme();
    const { anchorPosition, isOpen, handleOpen, handleClose } = useContextMenu(disabled);

    const [{ isDragging }, dragRef] = useDrag({
        type: itemType,
        item: { dragIds },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
        canDrag: !disabled,
    });

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (disabled) return;
        if (event.ctrlKey && event.key === 'b') onDuplicate();
        if (event.key === 'Delete') onDelete();
    };

    const menuActions: MenuItemDraggable[] = [
        ...menuItems,
        { label: t('duplicate'), shortcut: 'Ctrl + B', action: onDuplicate },
        { label: t('edit'), shortcut: capitalizeFirstLetter(t('doubleClick')), action: onDoubleClick },
        { label: t('delete'), shortcut: capitalizeFirstLetter(t('del')), action: onDelete },
    ];

    return (
        <Badge invisible={!warning} color="warning" badgeContent={warningChar} sx={{ width: '100%' }}>
            <Box
                ref={dragRef}
                onContextMenu={handleOpen}
                tabIndex={0}
                onKeyDown={handleKeyDown}
                onDragStart={!disabled ? onDragStart : undefined}
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    maxWidth: 500,
                    backgroundColor: getMaterialStateColor(state),
                    borderRadius: '10px',
                    flexGrow: 1,
                    minHeight: '25px',
                    opacity: isDragging ? 0.5 : 1,
                    cursor: disabled ? 'default' : 'pointer',
                    transform: 'translate3d(0, 0, 0)',
                    outline: warning ? '1px solid darkorange' : 'none',
                    '&:hover': !disabled ? { outline: `1px solid ${mode === 'dark' ? 'whitesmoke' : 'black'}` } : {},
                    '&:focus': !disabled ? { outline: `2px solid ${mode === 'dark' ? 'whitesmoke' : 'black'}` } : {},
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        userSelect: 'text',
                        px: 1,
                        display: 'flex',
                        alignItems: 'center',
                    }}
                    onDoubleClick={disabled ? undefined : onDoubleClick}
                >
                    {children}
                </Box>

                <Menu
                    open={!disableMenu && isOpen}
                    onClose={handleClose}
                    anchorReference="anchorPosition"
                    anchorPosition={anchorPosition || undefined}
                >
                    {menuActions.map(({ label, shortcut, action }) => (
                        <MenuItem
                            key={label}
                            onClick={() => {
                                handleClose();
                                action?.();
                            }}
                            sx={{ gap: 2 }}
                        >
                            {capitalizeFirstLetter(label)}
                            <Box component="span" sx={{ marginLeft: 'auto', opacity: 0.6, fontSize: '0.85em' }}>
                                {shortcut}
                            </Box>
                        </MenuItem>
                    ))}
                </Menu>
            </Box>
        </Badge>
    );
};

export default DraggableCell;
